import { TEST_HUB_URL } from '@/constants/constants';
import { apiFetch } from '@/utils/apiWrapper';
import { PAYMENT_CHANNELS, PaymentChannel } from '@final-commerce/common';

const pricingConfigurationsUrl = `${TEST_HUB_URL}pricing-configurations/`;

export type PricingValues = {
  variable: number;
  fixed: number;
  amex: number;
};

// From `@final-commerce/common` so the literals always match what hub-api validates and stores.
export type PricingChannel = PaymentChannel;
export const PRICING_CHANNEL_ORDER: readonly PricingChannel[] = PAYMENT_CHANNELS;

/** One stored rate: what a country costs on one channel. */
export type PricingRate = PricingValues & {
  countryCode: string;
  channel: PricingChannel;
};

/** One channel's rates, keyed by country. */
export type PricingConfigurationValues = Record<string, PricingValues>;

export type PricingConfiguration = {
  _id: string;
  organizationId: string;
  planId?: string;
  /** Optional: pre-split documents have no `rates` (the schema default applies on insert). Read via `toRates`. */
  rates?: PricingRate[];
  /** @deprecated Pre-split shape, still returned by un-migrated documents. Read via `toRates`. */
  values?: PricingConfigurationValues;
  createdAt?: string | Date;
  updatedAt?: string | Date;
};

export type CreatePricingConfigurationPayload = {
  rates: PricingRate[];
  /**
   * `updatedAt` this edit was based on; the server rejects the save with 409 if it changed. Needed
   * because a save sends the full list, including a possibly stale copy of the other channel.
   */
  expectedUpdatedAt?: string;
};

// The server equality-compares this against a BSON date, so keep millisecond ISO form
// (`String(date)` would never match).
export const versionOf = (config: PricingConfiguration | null | undefined): string | undefined => {
  const updatedAt = config?.updatedAt;
  if (!updatedAt) return undefined;
  return typeof updatedAt === 'string' ? updatedAt : updatedAt.toISOString();
};

// Checks the status `apiFetch` attaches, not the server-authored message text.
export const isPricingConflict = (error: unknown): boolean =>
  (error as { status?: number } | null)?.status === 409;

// A pre-split document has only `values`; it expands to identical rates on both channels,
// mirroring the server-side fallback.
export const toRates = (config: PricingConfiguration | null | undefined): PricingRate[] => {
  if (!config) return [];
  if (config.rates?.length) return config.rates;
  if (!config.values) return [];

  const expanded: PricingRate[] = [];
  for (const [countryCode, entry] of Object.entries(config.values)) {
    if (!entry || typeof entry.variable !== 'number') continue;
    for (const channel of PRICING_CHANNEL_ORDER) {
      expanded.push({
        // Uppercase country codes, as hub-api does: legacy keys like `ca` would render Canada twice.
        countryCode: countryCode.trim().toUpperCase(),
        channel,
        variable: entry.variable,
        fixed: entry.fixed,
        amex: entry.amex,
      });
    }
  }
  return expanded;
};

// A country with no row on this channel is skipped, not zero-filled, so it inherits; `{0,0,0}`
// could be saved as a real 0% rate.
export const channelValues = (
  config: PricingConfiguration | null | undefined,
  channel: PricingChannel,
): PricingConfigurationValues => {
  const values: PricingConfigurationValues = {};
  for (const rate of toRates(config)) {
    if (rate.channel !== channel) continue;
    values[rate.countryCode] = { variable: rate.variable, fixed: rate.fixed, amex: rate.amex };
  }
  return values;
};

/**
 * An old hub-api that ignores `rates` reports success and stores nothing — verify the response.
 * A pre-split build cannot echo `rates` (not a schema path), so its absence is exact. Pairs are
 * compared on country+channel only, since the server uppercases codes and numbers may reformat.
 */
const rateKey = (rate: { countryCode: string; channel: PricingChannel }) =>
  `${rate.countryCode.toUpperCase()}:${rate.channel}`;

export const assertRatesPersisted = (
  sent: PricingRate[],
  stored: PricingConfiguration | null | undefined,
): PricingConfiguration => {
  if (!stored || !Array.isArray(stored.rates)) {
    throw new Error(
      'Pricing was not saved: the server accepted the request but returned no rate list, which means ' +
        'it is a pre-split build that ignored `rates`. hub-api must be deployed before this app.',
    );
  }
  const storedKeys = new Set(stored.rates.map(rateKey));
  const missing = sent.filter((rate) => !storedKeys.has(rateKey(rate)));
  if (missing.length) {
    throw new Error(`Pricing was not saved in full: the server did not store ${missing.map(rateKey).join(', ')}.`);
  }
  return stored;
};

export const upsertOrganizationPricingConfiguration = async (params: {
  organizationId: string;
  payload: CreatePricingConfigurationPayload;
}): Promise<PricingConfiguration> => {
  const { organizationId, payload } = params;
  const url = `${pricingConfigurationsUrl}${organizationId}`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response) throw new Error('Failed to upsert organization pricing configuration');
  return assertRatesPersisted(payload.rates, (await response.json()) as PricingConfiguration);
};

export const upsertPlanPricingConfiguration = async (params: {
  organizationId: string;
  planId: string;
  payload: CreatePricingConfigurationPayload;
}): Promise<PricingConfiguration> => {
  const { organizationId, planId, payload } = params;
  const url = `${pricingConfigurationsUrl}${organizationId}/${planId}`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response) throw new Error('Failed to upsert plan pricing configuration');
  return assertRatesPersisted(payload.rates, (await response.json()) as PricingConfiguration);
};

export const getPricingConfiguration = async (params: {
  organizationId: string;
  planId?: string;
}): Promise<PricingConfiguration | null> => {
  const { organizationId, planId } = params;
  const query = new URLSearchParams();
  if (planId) query.append('planId', planId);
  const qs = query.toString();
  const url = `${pricingConfigurationsUrl}${organizationId}${qs ? `?${qs}` : ''}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch pricing configuration');
  const data = (await response.json()) as PricingConfiguration[] | PricingConfiguration;
  if (Array.isArray(data)) return data[0] ?? null;
  return data;
};

export const getDefaultPricingConfiguration = async (params: {
  organizationId: string;
}): Promise<PricingConfiguration> => {
  const { organizationId } = params;
  const url = `${pricingConfigurationsUrl}defaults/${organizationId}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch default pricing configuration');
  return response.json();
};

export const getMinimumPricingConfiguration = async (params: {
  organizationId: string;
}): Promise<PricingConfiguration> => {
  const { organizationId } = params;
  const url = `${pricingConfigurationsUrl}minimum/${organizationId}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response) throw new Error('Failed to fetch minimum pricing configuration');
  return response.json();
};

export const upsertMinimumPricingConfiguration = async (params: {
  organizationId: string;
  payload: CreatePricingConfigurationPayload;
}): Promise<PricingConfiguration> => {
  const { organizationId, payload } = params;
  const url = `${pricingConfigurationsUrl}minimum/${organizationId}`;
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response) throw new Error('Failed to upsert minimum pricing configuration');
  return assertRatesPersisted(payload.rates, (await response.json()) as PricingConfiguration);
};
